import { Component, OnInit, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, from } from 'rxjs';
import { map } from 'rxjs/operators';

import { PropertyService } from '../../services/property.service';

@Component({
  selector: 'app-property-internal-data',
  templateUrl: './property-internal-data.component.html',
  styleUrls: ['./property-internal-data.component.scss']
})
export class PropertyInternalDataComponent implements OnInit, OnChanges {
  @Input() property;
  @Input() showLinks = true;

  conversations;
  invoiceSummary: Observable<any>;

  lease;
  hasActiveLease$: Observable<boolean>;

  constructor(private propertyService: PropertyService,
              private router: Router) { }

  ngOnInit() {
    this.loadData();
  }

  ngOnChanges(changes: SimpleChanges) {
    // Reload data when property input changes
    if (changes['property'] && !changes['property'].firstChange) {
      this.loadData();
    }
  }

  private loadData() {
    if(this.property && this.property.id) {
      this.conversations = this.propertyService.getConversations(this.property, "UNREAD", 3);
      this.lease = this.propertyService.getActiveLease(this.property);
      // Availability is derived from the active lease, not a stored status field
      this.hasActiveLease$ = from(Promise.resolve(this.lease)).pipe(map(lease => !!lease));
      // Convert Promise to Observable for async pipe, default to 0 if error
      this.invoiceSummary = from(
        this.propertyService.getInvoiceSummary(this.property)
          .then((summary: any) => {
            // Ensure balanceDue is a number
            const balance = summary && summary.balanceDue ? parseFloat(summary.balanceDue) : 0;
            return { balanceDue: balance };
          })
          .catch((error) => {
            console.error('Error loading invoice summary:', error);
            return { balanceDue: 0 };
          })
      );
    } else {
      // Initialize with default value if no property ID
      this.invoiceSummary = from(Promise.resolve({ balanceDue: 0 }));
    }
  }

  respondAction(conversation) {
    this.router.navigate(['message-respond'], { queryParams: { propertyId: this.property.id, conversationId: conversation.id } });
  }

  messagesAction() {
    this.router.navigate(['all-messages'], { queryParams: { propertyId: this.property.id } });
  }

  startLeaseAction() {
    this.router.navigate(['start-lease'], { queryParams: { propertyId: this.property.id } });
  }

  modifyLeaseAction() {
    const queryParams: any = { propertyId: this.property.id };
    Promise.resolve(this.lease).then((activeLease) => {
      if (activeLease && activeLease.id) {
        queryParams.leaseId = activeLease.id;
      }
      this.router.navigate(['modify-lease'], { queryParams });
    }).catch(() => {
      this.router.navigate(['modify-lease'], { queryParams });
    });
  }

  getInvoiceSummary() {
    return this.propertyService.getInvoiceSummary(this.property);
  }

  invoicesAction() {
    this.router.navigate(['admin-invoices'], { queryParams: { propertyId: this.property.id } });
  }

  externalPaymentAction() {
    this.router.navigate(['external-payment'], { queryParams: { propertyId: this.property.id } });
  }

  contactInfoAction() {
    this.router.navigate(['contact-info'], { queryParams: { propertyId: this.property.id } });
  }

  manageLeasesAction() {
    this.router.navigate(['manage-leases'], { queryParams: { propertyId: this.property.id } });
  }

  expensesAction() {
    this.router.navigate(['property-expenses'], { queryParams: { propertyId: this.property.id } });
  }

  statementsAction() {
    this.router.navigate(['property-statements'], { queryParams: { propertyId: this.property.id } });
  }
}
